import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Compass, Sparkles, LifeBuoy, ClipboardList, GraduationCap, ClipboardCheck, FileText, ShieldCheck, MessageSquareOff } from "lucide-react";
import TrilliumIcon from "./TrilliumIcon";
import GeneratorDemo from "./GeneratorDemo";
import SkillInTheWildCard from "./SkillInTheWildCard";
import SkillInTheWildImage from "./SkillInTheWildImage";
import UserMenu from "./UserMenu";
import StartBuildingButton from "./StartBuildingButton";
import FreeGenerator from "./FreeGenerator";
import FeaturedLessonCard from "./FeaturedLessonCard";
import SamplePreviewStrip from "./SamplePreviewStrip";
import {
  HERO_PRESETS,
  DEFAULT_HERO_PRESET,
  HERO_PRESET_STORAGE_KEY,
  getHeroPreset,
} from "./homepageHero";
import { fetchHomepageHeroPreset } from "../lib/api";
import { getClaimedFreeLesson, hasClaimedFreeLesson } from "../lib/freeLessonMarker";
// EarlyAccessCTA removed Feb 2026 launch — see also AtomicEdu hero render.

import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import useDocumentMeta from "../lib/useDocumentMeta";

// Phase 137 — FrameworkColumn + ValueRow helpers retired with the
// Scan/Process/Execute and Core Value sections in the homepage
// restructure. If the framework storytelling reappears on /the-path,
// recreate the helper there.

export const AtomicEdu = () => {
  const [coverageBump, setCoverageBump] = useState(0);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useDocumentMeta({
    title: "Atomic Edu — Plan, teach, assess, and report in one place",
    description:
      "Lessons, units, rubrics, tests, and Ontario report-card comments for Grades 4–8 — all in one place, so you walk into class ready to teach.",
    image: "https://atomicedu.ca/og/home.png",
    url: "https://atomicedu.ca/",
    type: "website",
  });

  // Lane 2 (Feb 2026) — seasonal hero preset. Zero-flash strategy:
  //   1. Initialise state SYNCHRONOUSLY from localStorage cache so
  //      returning visitors see the correct preset on first paint.
  //   2. First-time visitors default to Preset A (Summer / General) —
  //      the safe general fallback specified by the founder.
  //   3. After mount, quietly fetch the live server value and update
  //      only if it disagrees. Zero visible flicker for returning
  //      visitors; a single one-time swap for a first-timer whose
  //      current active preset is B.
  const [heroPresetKey, setHeroPresetKey] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_HERO_PRESET;
    try {
      const cached = window.localStorage.getItem(HERO_PRESET_STORAGE_KEY);
      if (cached && Object.prototype.hasOwnProperty.call(HERO_PRESETS, cached)) {
        return cached;
      }
    } catch (_e) {
      /* localStorage disabled — fall through to default */
    }
    return DEFAULT_HERO_PRESET;
  });

  useEffect(() => {
    let cancelled = false;
    fetchHomepageHeroPreset()
      .then((data) => {
        if (cancelled) return;
        const serverPreset =
          data && Object.prototype.hasOwnProperty.call(HERO_PRESETS, data.preset)
            ? data.preset
            : DEFAULT_HERO_PRESET;
        if (serverPreset !== heroPresetKey) {
          setHeroPresetKey(serverPreset);
        }
        try {
          window.localStorage.setItem(HERO_PRESET_STORAGE_KEY, serverPreset);
        } catch (_e) {
          /* localStorage disabled — silently ignore */
        }
      })
      .catch(() => {
        // Fetch failure: keep whatever we rendered. Never fail-noisy on
        // the marketing page.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeHero = getHeroPreset(heroPresetKey);

  // Lane 3A (Feb 2026) — Returning-marked-browser signal. Read once
  // on mount for anonymous visitors; signed-in users skip entirely
  // (they see their own experience). Soft UX signal, defeatable —
  // NOT a server gate.
  const [claimedTaste, setClaimedTaste] = useState(null);
  useEffect(() => {
    if (user) {
      setClaimedTaste(null);
      return;
    }
    if (hasClaimedFreeLesson()) {
      setClaimedTaste(getClaimedFreeLesson());
    }
  }, [user]);
  const hasTasted = !!claimedTaste && !user;

  // Phase 115 follow-up — if a teacher is already signed in and lands
  // on the marketing home (post-OAuth redirect, deep link from another
  // tab, etc.) send them straight to their library where the left rail
  // gives them lateral access to every tool. The marketing page is for
  // logged-out visitors. We honor `?stay=1` for the rare case where
  // a signed-in teacher genuinely wants to see the public landing.
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("stay") === "1") {
      return;
    }
    navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  // Bump SITW refresh key when auth state changes (anonymous -> signed
  // in or vice versa) so the sidebar reflects the right teacher's state.
  useEffect(() => {
    setCoverageBump((n) => n + 1);
  }, [user?.user_id]);

  return (
    <div className="relative min-h-screen text-[#4a6984] overflow-x-clip">
      {/* PERSISTENT BACKGROUND ACCENTS — stay in place while the page scrolls */}
      <div
        aria-hidden="true"
        className="fixed -top-32 -right-32 w-[44rem] h-[44rem] rounded-full bg-[#96ded1]/30 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed top-1/2 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#96ded1]/15 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed -bottom-40 right-1/4 w-[30rem] h-[30rem] rounded-full bg-[#96ded1]/20 blur-3xl pointer-events-none z-0"
      />

      {/* NAV */}
      <header
        data-testid="site-nav"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#1c2d3d]/95 border-b border-[#1c2d3d] shadow-lg shadow-[#1c2d3d]/10"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 shrink-0">
            <a
              href="#top"
              data-testid="brand-logo"
              className="font-black text-sm sm:text-base tracking-tight text-white/80 hover:text-white inline-flex items-center gap-2 whitespace-nowrap shrink-0"
            >
              <img
                src="/brand/atomicedu-logo-mark.png"
                alt="Atomic Edu"
                width="36"
                height="36"
                className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0"
                data-testid="brand-logo-mark"
              />
              Atomic Edu
            </a>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Link
              to="/samples"
              data-testid="nav-samples-link"
              className="hidden lg:inline-flex items-center px-2.5 py-1.5 rounded-full text-[13px] font-extrabold tracking-tight text-white/90 hover:text-white hover:bg-white/10 transition"
            >
              See Sample Lessons
            </Link>
            <Link
              to="/pricing"
              data-testid="nav-pricing-link"
              className="hidden lg:inline-flex items-center px-2.5 py-1.5 rounded-full text-[13px] font-extrabold tracking-tight text-white/90 hover:text-white hover:bg-white/10 transition"
            >
              Plans
            </Link>
            {/* Start Building hides below lg now — UserMenu already
                packs Skills Library / Why Skills / The Path / Sign in
                onto the right side at md, and adding 3 more CTAs at md
                overflows into the brand wordmark. The sticky bottom
                CTA covers the conversion job for narrower viewports. */}
            <span className="hidden lg:inline-flex">
              <StartBuildingButton testid="nav-start-building" />
            </span>
            <UserMenu />
          </div>
        </div>
      </header>

      {/* Classroom Support Hub was previously the top-of-page hero
          (Phase 126.I). Moved to after "Growth Over Time" in
          Phase 133 — the marketing flow now leads with the
          one-place product story, then descends through framework /
          generator demo / growth before introducing the Hub as a
          deepening signal of pedagogical seriousness. */}

      {/* HERO */}
      <section
        id="top"
        data-testid="hero-section"
        className="relative pt-20 pb-10 md:pt-24 md:pb-14 overflow-hidden z-10"
      >
        <div className="absolute inset-0 ae-hero-grid opacity-50 pointer-events-none" />
        {/* Brand watermark — large, low-opacity logo anchored right of hero.
            Width/height attrs reserve aspect-ratio space so the browser
            doesn't shift surrounding content when the asset finally loads
            (CLS fix). Only renders at lg+ via Tailwind. */}
        <img
          src="/brand/atomicedu-logo.png"
          alt=""
          aria-hidden="true"
          data-testid="hero-watermark"
          width="640"
          height="640"
          className="hidden lg:block pointer-events-none select-none absolute -right-24 top-1/2 -translate-y-1/2 w-[640px] h-auto opacity-[0.06]"
        />
        <div className="relative max-w-7xl mx-auto px-6">
          <span
            data-testid="hero-kicker"
            className="inline-flex items-center gap-2 rounded-full border border-[#4a6984]/15 bg-white/60 backdrop-blur-sm px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/80"
          >
            <TrilliumIcon size={12} className="text-[#4a6984]" />
            Ontario · Grades 4–8 · One place
          </span>

          <div className="mt-10 max-w-3xl">
            {/* Phase 137 — Single-column teacher hero. The "Why skills?"
                right column was retired with the homepage restructure;
                that argument now lives on /the-path. */}
            <div data-testid="hero-teacher-column">
              <h1
                data-testid="hero-heading"
                data-hero-preset={heroPresetKey}
                className="font-black text-3xl sm:text-4xl lg:text-5xl text-[#4a6984] tracking-tighter leading-[1.05]"
              >
                {activeHero.headline}
              </h1>
              <p
                data-testid="hero-subheading"
                className="mt-6 text-[#4a6984]/85 font-medium text-base sm:text-lg leading-relaxed"
              >
                {activeHero.subhead}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  asChild
                  data-testid="hero-cta"
                  className="ae-cta h-12 px-8 rounded-lg text-base inline-flex items-center gap-2"
                >
                  <Link to={user ? "/generator" : "/pricing"}>
                    {user
                      ? "Experience Atomic Edu"
                      : "Start your 7-day free trial"}{" "}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <a
                  href="#classroom-support-hub"
                  data-testid="hero-secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById("classroom-support-hub");
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                  }}
                  className="text-sm font-semibold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline cursor-pointer"
                >
                  Try a free utility →
                </a>
                <Link
                  to="/reflection"
                  data-testid="hero-reflection-link"
                  className="text-sm font-semibold text-[#3EB489] hover:text-[#2a7d6b] underline-offset-4 hover:underline"
                >
                  Year-end reflection — free →
                </Link>
              </div>
            </div>
          </div>

          <div
            data-testid="hero-stats"
            className="mt-16 md:mt-20 mb-4 grid grid-cols-3 gap-x-8 sm:gap-x-10 md:gap-x-12 gap-y-6 max-w-2xl"
          >
            <div>
              <p className="font-black text-4xl sm:text-5xl md:text-6xl text-slate-navy tracking-tight leading-none">60s</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] font-bold text-slate-navy">
                Avg. plan time
              </p>
            </div>
            <div>
              <p className="font-black text-4xl sm:text-5xl md:text-6xl text-slate-navy tracking-tight leading-none">100%</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] font-bold text-slate-navy">
                Curriculum aligned
              </p>
            </div>
            <div>
              <p className="font-black text-4xl sm:text-5xl md:text-6xl text-slate-navy tracking-tight leading-none">4–8</p>
              <p className="mt-3 text-xs uppercase tracking-[0.18em] font-bold text-slate-navy">
                Built for Ontario
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* This Week's Featured Lesson — surfaced on the public homepage
          for logged-out visitors (Jun 2026), directly under the hero
          stats. Self-hides when the admin hasn't pinned a lesson (the
          card renders null in that case). */}
      <section
        data-testid="home-featured-lesson-section"
        className="relative z-10"
      >
        <div className="max-w-3xl mx-auto px-6">
          <FeaturedLessonCard />
        </div>
      </section>

      {/* Phase 137 — "Built by a teacher" trust statement. Sits
          directly under the hero stats, before any product framing.
          Plain prose, no card treatment, left-aligned per spec. */}
      <section
        data-testid="who-built-this-section"
        aria-labelledby="who-built-this-heading"
        className="relative z-10 py-10 md:py-12 border-t border-[#4a6984]/10"
      >
        <div className="max-w-[640px] mx-auto px-6">
          {/* Phase 137.2 — Left-aligned with a 3px mint left border per
              the visual upgrade spec. Padding between border and text
              is 16px (pl-4). Body bumped to ~17-18px for comfortable
              reading. */}
          <div className="border-l-[3px] border-mint pl-4">
            <p
              id="who-built-this-heading"
              data-testid="who-built-this-kicker"
              className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-navy/70"
            >
              Who built this
            </p>
            <p
              data-testid="who-built-this-body"
              className="mt-4 text-slate-navy font-medium text-[17px] sm:text-lg leading-relaxed"
            >
              I&rsquo;ve taught Grades 4&ndash;8 in Simcoe County for 20 years —
              the same grades, the same Ontario curriculum, the same Sunday nights.
              I built Atomic Edu because the tools that were supposed to help
              never ran the whole program. This one does.
              <br />
              <span
                data-testid="who-built-this-signature"
                className="mt-3 inline-block font-extrabold text-slate-navy"
              >
                — Dan
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Phase 133 — Differentiator band. Sits flush under the hero
          and answers the implicit "but I already have ChatGPT" question
          in three bullets before the visitor scrolls into framework
          territory. Strength-first product positioning.
          Phase 137.2 — Visual upgrade: slate-navy section background,
          three coloured cards (mint / white / amber), 16px gap,
          equal-height (items-stretch + flex h-full). */}
      <section
        data-testid="differentiator-band"
        aria-labelledby="differentiator-heading"
        className="relative z-10 py-16 md:py-20 bg-slate-navy"
      >
        <div className="max-w-7xl mx-auto px-6">
          <h2
            id="differentiator-heading"
            className="font-bold uppercase text-xs tracking-[0.25em] text-white/85 text-center"
          >
            Why teachers choose Atomic Edu
          </h2>
          <div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch"
            data-testid="differentiator-grid"
          >
            <div
              data-testid="differentiator-card-1"
              className="h-full rounded-2xl p-7 md:p-8 bg-white flex flex-col"
            >
              <h3 className="font-extrabold text-xl md:text-[22px] text-slate-navy tracking-tight leading-tight">
                One place, every artifact.
              </h3>
              <p className="mt-3 text-[15px] text-slate-navy/90 leading-relaxed">
                Lessons, units, assessments, planning, gradebook, and
                Ontario-format report card comments all live together —
                no juggling six tools.
              </p>
            </div>
            <div
              data-testid="differentiator-card-2"
              className="h-full rounded-2xl p-7 md:p-8 bg-white flex flex-col"
            >
              <h3 className="font-extrabold text-xl md:text-[22px] text-slate-navy tracking-tight leading-tight">
                Ontario-aligned end-to-end.
              </h3>
              <p className="mt-3 text-[15px] text-slate-navy/90 leading-relaxed">
                Every output tied to specific Ontario expectations and
                Transferable Skills — what teachers and admin already
                read, recognise, and trust.
              </p>
            </div>
            <div
              data-testid="differentiator-card-3"
              className="h-full rounded-2xl p-7 md:p-8 bg-mint flex flex-col"
            >
              <h3 className="font-extrabold text-xl md:text-[22px] text-slate-navy tracking-tight leading-tight">
                Reclaim your evenings.
              </h3>
              <p className="mt-3 text-[15px] text-slate-navy/90 leading-relaxed">
                Outputs are short, classroom-ready, and printable in
                seconds — so the prep work that ate your weekend stops
                eating your weekend.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Phase 137 — "Already have resources?" — bring-your-own card.
          Phase 137.2 — Visual upgrade: slate-navy banner card (wider
          than the differentiator cards above), white type, mint CTA. */}
      <section
        data-testid="byo-resources-section"
        aria-labelledby="byo-resources-heading"
        className="relative z-10 py-12 md:py-16"
      >
        <div className="max-w-5xl mx-auto px-6">
          <div
            data-testid="byo-resources-card"
            className="rounded-2xl p-7 sm:p-10 md:p-12 bg-slate-navy shadow-xl"
          >
            <h3
              id="byo-resources-heading"
              className="font-extrabold text-[22px] sm:text-2xl md:text-[26px] text-white tracking-tight leading-tight"
            >
              Already have resources?
            </h3>
            <p className="mt-4 text-[15px] sm:text-base text-white/90 leading-relaxed max-w-3xl">
              Upload any reading, worksheet, or classroom material and
              Atomic Edu builds a complete, differentiated lesson around
              it — simplified for your striving readers, extended for your
              gifted ones, and tied to the Ontario curriculum your class
              is covering.
            </p>
            {/* Phase 137.1 — CTA button (mint bg, slate-navy text).
                /lesson-from-resource is the dedicated upload landing
                page (renamed from /try, Jul 2026 SEO URLs lane); this
                card is the ONLY entry point to it from the marketing
                homepage. Points at the canonical URL directly rather
                than the /try soft-redirect so returning teachers skip
                the hop. */}
            <div className="mt-7">
              <Link
                to="/lesson-from-resource"
                data-testid="byo-resources-cta"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-lg text-sm font-extrabold bg-mint text-slate-navy hover:bg-mint-500 transition-colors"
              >
                Upload a resource <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* LOGGED-IN WELCOME QUICK ACTIONS (Phase 124)
          Two prominent shortcuts a returning teacher wants immediately:
          (a) generate a lesson around today's skill,
          (b) jump into the sample plans library.
          Hidden when signed-out so marketing visitors aren't confused. */}
      {user && (
        <section
          data-testid="home-loggedin-quick-actions"
          className="relative z-10 pt-8 pb-4 md:pt-10 md:pb-6 border-t border-[#4a6984]/5"
        >
          <div className="max-w-5xl mx-auto px-6">
            <div className="mb-4 flex items-baseline justify-between gap-4 flex-wrap">
              <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
                Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}
              </p>
              <Link
                to="/dashboard?view=library"
                data-testid="home-quick-library-link"
                className="text-xs font-semibold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline inline-flex items-center gap-1"
              >
                Open My Library <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* (a) Skill Lesson of the Day — sends the teacher into
                  the Generator at /generator. The Generator reads
                  ?skill_of_day=1 and pre-fills today's Skill in the
                  Wild via the existing /api/skill-in-the-wild
                  endpoint (added in this phase). */}
              <Link
                to="/generator?skill_of_day=1"
                data-testid="home-quick-skill-of-day"
                className="group ae-card p-5 hover:shadow-md transition-shadow duration-200 flex items-start gap-4"
              >
                <div className="shrink-0 h-11 w-11 rounded-xl bg-[#96ded1]/40 border border-[#96ded1]/70 grid place-items-center">
                  <Sparkles className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Today&apos;s pick
                  </p>
                  <h3 className="mt-0.5 font-extrabold text-lg text-[#1c2d3d]">
                    Skill Lesson of the Day
                  </h3>
                  <p className="mt-1 text-sm text-[#4a6984] leading-snug">
                    Jump straight into the Generator with today&apos;s Skill in the Wild pre-filled.
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#1c2d3d] group-hover:gap-2 transition-all">
                    Build now <ArrowRight className="h-3.5 w-3.5" />
                  </p>
                </div>
              </Link>
              {/* (b) Sample Plans — direct route to /samples so
                  teachers can browse ready-to-print examples for
                  ideas, scope, or starter templates. */}
              <Link
                to="/samples"
                data-testid="home-quick-sample-plans"
                className="group ae-card p-5 hover:shadow-md transition-shadow duration-200 flex items-start gap-4"
              >
                <div className="shrink-0 h-11 w-11 rounded-xl bg-[#f4843b]/20 border border-[#f4843b]/40 grid place-items-center">
                  <Compass className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Inspiration
                  </p>
                  <h3 className="mt-0.5 font-extrabold text-lg text-[#1c2d3d]">
                    Sample Plans
                  </h3>
                  <p className="mt-1 text-sm text-[#4a6984] leading-snug">
                    Browse Ontario-aligned sample lessons, units, tests, and tasks. Copy any to your library.
                  </p>
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#1c2d3d] group-hover:gap-2 transition-all">
                    Browse samples <ArrowRight className="h-3.5 w-3.5" />
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SKILLS FIRST — the four Skills-surface entry points. Sits
          above the free-lesson section so both anonymous and signed-
          in viewers see it. Unifies /skills, /why-skills, /the-path,
          and /posters into a single scannable band (previously each
          lived on its own scroll depth). Founder-approved lane
          (Jul 12 2026) — the only new marketing surface allowed
          during the launch freeze. */}
      <section
        data-testid="home-skills-first-section"
        className="relative z-10 pt-16 pb-4 md:pt-20 md:pb-6"
      >
        <div className="max-w-5xl mx-auto px-6">
          <p
            data-testid="home-skills-first-kicker"
            className="font-bold uppercase text-[11px] tracking-[0.22em] text-[#96ded1] text-center"
          >
            Skills First
          </p>
          <h2
            data-testid="home-skills-first-heading"
            className="mt-3 font-black text-3xl sm:text-4xl text-[#1c2d3d] tracking-tight text-center"
          >
            The whole skills surface, in four doors.
          </h2>
          <p
            data-testid="home-skills-first-subhead"
            className="mt-4 max-w-2xl mx-auto text-center text-[#4a6984]/85 font-medium text-base leading-relaxed"
          >
            Everything Atomic Edu builds into every lesson — the seven
            transferable skills, the manifesto, the thread from classroom to
            career, and the wall posters — one click away.
          </p>

          <div
            data-testid="home-skills-first-grid"
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          >
            <Link
              to="/skills"
              data-testid="home-skills-first-btn-library"
              className="ae-card group p-5 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all"
            >
              <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#96ded1]">
                Playbooks
              </p>
              <p className="mt-2 font-black text-lg text-[#1c2d3d] tracking-tight">
                The Skills Library
                <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </p>
              <p className="mt-2 text-sm text-[#4a6984] font-medium leading-snug">
                Playbooks for all eight skills, ready to teach tomorrow.
              </p>
            </Link>

            <Link
              to="/why-skills"
              data-testid="home-skills-first-btn-why"
              className="ae-card group p-5 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all"
            >
              <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#96ded1]">
                Manifesto
              </p>
              <p className="mt-2 font-black text-lg text-[#1c2d3d] tracking-tight">
                Why skills?
                <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </p>
              <p className="mt-2 text-sm text-[#4a6984] font-medium leading-snug">
                The case for teaching transferable skills — until now.
              </p>
            </Link>

            <Link
              to="/the-path"
              data-testid="home-skills-first-btn-path"
              className="ae-card group p-5 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all"
            >
              <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#96ded1]">
                The Thread
              </p>
              <p className="mt-2 font-black text-lg text-[#1c2d3d] tracking-tight">
                The Path
                <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </p>
              <p className="mt-2 text-sm text-[#4a6984] font-medium leading-snug">
                From your classroom to their future — in seven skills.
              </p>
            </Link>

            <Link
              to="/posters"
              data-testid="home-skills-first-btn-posters"
              className="ae-card group p-5 border border-[#4a6984]/15 hover:border-[#1c2d3d]/40 hover:shadow-md transition-all"
            >
              <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#96ded1]">
                Print
              </p>
              <p className="mt-2 font-black text-lg text-[#1c2d3d] tracking-tight">
                Free posters
                <span className="ml-1 inline-block transition-transform group-hover:translate-x-0.5">
                  →
                </span>
              </p>
              <p className="mt-2 text-sm text-[#4a6984] font-medium leading-snug">
                Seven transferable-skill posters for the classroom wall.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FREE LESSON SECTION — primary on-page conversion for ANONYMOUS
          visitors. Phase 152.Y.1: hidden entirely for signed-in viewers
          (they have the in-app Generator in their AppShell sidebar — a
          marketing capture-form on their own homepage is just noise).
          The marketing CTA below (`below-video-cta`) is also already
          conditional on auth state — the homepage flows naturally for
          returning teachers without this card. */}
      {!user && (
        <section
          id="try-it"
          data-testid="home-free-lesson-section"
          className="relative z-10 pt-4 pb-12 md:pt-8 md:pb-16"
        >
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-8" data-testid="home-try-it-intro">
              <h2 className="font-black text-3xl sm:text-4xl text-[#1c2d3d] tracking-tighter">
                Try the generator &mdash; free
              </h2>
              <p className="mt-4 text-[#4a6984] font-medium text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
                Build a complete, curriculum-aligned Ontario lesson package. Same
                generator paid subscribers use, nothing held back. We&apos;ll email
                you the PDF &mdash; one free lesson a week, no account needed.
              </p>
            </div>
            {hasTasted && (
              <div
                data-testid="home-free-lesson-returning"
                className="ae-card p-6 md:p-8 text-center"
              >
                <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#96ded1]">
                  You&apos;ve had your free taste
                </p>
                <h3 className="mt-3 font-extrabold text-2xl sm:text-3xl text-[#1c2d3d] tracking-tight">
                  Ready for the full version?
                </h3>
                <p className="mt-4 text-[#4a6984] font-medium text-base leading-relaxed">
                  Start a 7-day free trial for the full engine — slide decks,
                  differentiation, and unlimited planning.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 justify-center">
                  <Link
                    to="/pricing"
                    data-testid="home-free-lesson-returning-trial"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition"
                  >
                    Start your 7-day free trial <ArrowRight className="h-4 w-4" />
                  </Link>
                  {claimedTaste?.token && (
                    <Link
                      to={`/free-lesson/${encodeURIComponent(claimedTaste.token)}`}
                      data-testid="home-free-lesson-returning-open"
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#1c2d3d] font-bold text-sm border border-[#4a6984]/25 hover:bg-[#96ded1]/15 transition"
                    >
                      Open your printed lesson →
                    </Link>
                  )}
                </div>
              </div>
            )}
            <FreeGenerator />
            <p
              data-testid="home-free-lesson-social-proof"
              className="mt-6 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/55"
            >
              Ontario curriculum-aligned <span className="mx-2">·</span>
              Grades 4–8 <span className="mx-2">·</span>
              Skills-first pedagogy <span className="mx-2">·</span>
              7-day free trial
            </p>
          </div>
        </section>
      )}

      {/* Phase 137 — FRAMEWORK (Scan / Process / Execute) section
          removed per homepage restructure. The argument now lives on
          /the-path. The page jumps directly from the free generator
          + demo to the on-page proof (Career Connection, Hub). */}

      {/* GENERATOR DEMO — auto-playing visual proof of the three-step promise */}
      <GeneratorDemo />

      {/* Homepage sample-package preview strip — page-1 tiles of the
          featured lesson's seven artifacts, every tile links to /samples.
          Replaces the old plain text link (Lane B, 2026). */}
      <SamplePreviewStrip />

      {/* ============================================================
          LANE 1 — Homepage rebuild (Feb 2026). Sections inserted in
          this order per approved plan:
            (a) 4-step "How it works" loop + honest 7-day trial caption
            (b) Privacy strip (No student names. No student accounts.)
            (c) "Why not just use ChatGPT?" objection block
            (d) Proof strip (honest single line — no fabricated quotes)
            (e) Pricing teaser (no dollar figures, no dates)
          Replaces the old "Start building from $0 — see plans" link.
      ============================================================ */}

      {/* (a) 4-step loop */}
      <section
        data-testid="how-it-works-loop-section"
        aria-labelledby="how-it-works-loop-heading"
        className="relative z-10 py-16 md:py-20 border-t border-[#4a6984]/5"
      >
        <div className="max-w-6xl mx-auto px-6">
          <h2
            id="how-it-works-loop-heading"
            className="text-center font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
          >
            How it works
          </h2>
          <div
            data-testid="how-it-works-loop-grid"
            className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {[
              {
                key: "plan",
                icon: ClipboardList,
                heading: "Plan",
                body:
                  "Lessons built for your grade, your subject, and the skill you're teaching — not a template someone else made.",
              },
              {
                key: "teach",
                icon: GraduationCap,
                heading: "Teach",
                body:
                  "Your planner and coverage tracker keep the record as you go.",
              },
              {
                key: "assess",
                icon: ClipboardCheck,
                heading: "Assess",
                body:
                  "Exit tickets and level marks, Growing Success logic built in. AI suggests; you confirm.",
              },
              {
                key: "report",
                icon: FileText,
                heading: "Report",
                body:
                  "Comments written from what you actually taught. Your character limits. Your language.",
              },
            ].map(({ key, icon: Icon, heading, body }, i) => (
              <div
                key={key}
                data-testid={`how-it-works-loop-step-${key}`}
                className="ae-card p-6 md:p-7 flex flex-col h-full"
              >
                <div className="flex items-center gap-3">
                  <div className="shrink-0 h-10 w-10 rounded-xl bg-[#96ded1]/30 border border-[#96ded1]/60 grid place-items-center">
                    <Icon className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
                  </div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Step {i + 1}
                  </p>
                </div>
                <h3 className="mt-4 font-extrabold text-xl text-[#1c2d3d] tracking-tight">
                  {heading}
                </h3>
                <p className="mt-3 text-[15px] text-[#4a6984] font-medium leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
          {/* Honest 7-day trial caption. Calm fact, not disclaimer. */}
          {/* Source of truth for tier name "Pro":
              /app/backend/subscription_tiers.py :: TIERS["pro"]["name"].
              Hardcoded here to keep the marketing page network-free;
              revisit if we ever rename the tier. */}
          <p
            data-testid="how-it-works-loop-caption"
            className="mt-10 text-center text-[#4a6984] font-medium text-[15px] md:text-base leading-relaxed max-w-2xl mx-auto"
          >
            The full loop is yours to try free for 7 days. Assessment tools like
            the Gradebook live in <span className="font-extrabold text-[#1c2d3d]">Pro</span>.
          </p>
        </div>
      </section>

      {/* (b) Privacy strip — full-width, high-contrast */}
      <section
        data-testid="home-privacy-strip"
        aria-labelledby="home-privacy-strip-heading"
        className="relative z-10 py-10 md:py-12 bg-slate-navy"
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2
            id="home-privacy-strip-heading"
            className="font-extrabold text-xl sm:text-2xl md:text-[26px] text-white tracking-tight leading-tight inline-flex items-center gap-3 flex-wrap justify-center"
          >
            <ShieldCheck className="h-6 w-6 text-mint shrink-0" strokeWidth={2} />
            No student names. No student accounts. Ever.
          </h2>
          <p
            data-testid="home-privacy-strip-body"
            className="mt-4 text-white/90 font-medium text-[15px] md:text-base leading-relaxed max-w-3xl mx-auto"
          >
            Anonymous slots you label. Content processed and discarded — never
            stored.{" "}
            <Link
              to="/privacy"
              data-testid="home-privacy-strip-how-it-works"
              className="text-mint font-bold underline underline-offset-4 hover:text-white transition-colors"
            >
              How it works →
            </Link>
          </p>
        </div>
      </section>

      {/* (c) "Why not just use ChatGPT?" objection block */}
      <section
        data-testid="home-chatgpt-block"
        aria-labelledby="home-chatgpt-block-heading"
        className="relative z-10 py-16 md:py-20 border-t border-[#4a6984]/5"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#f4843b]/15 border border-[#f4843b]/40 grid place-items-center shrink-0">
              <MessageSquareOff className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
            </div>
            <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70">
              Fair question
            </p>
          </div>
          <h2
            id="home-chatgpt-block-heading"
            className="mt-5 font-extrabold text-2xl sm:text-3xl md:text-[32px] text-[#1c2d3d] tracking-tight leading-[1.15]"
          >
            Why not just use ChatGPT?
          </h2>
          <p
            data-testid="home-chatgpt-block-body"
            className="mt-6 text-[#4a6984] font-medium text-base md:text-lg leading-relaxed"
          >
            ChatGPT — or any general AI — doesn&apos;t know your long range
            plan. It doesn&apos;t know what you taught in October. It
            doesn&apos;t know Growing Success language, Learning Skills
            look-fors, or that your board&apos;s comment box cuts off at 1,200
            characters.
          </p>
          <p
            data-testid="home-chatgpt-block-punchline"
            className="mt-5 font-extrabold text-lg md:text-xl text-[#1c2d3d] leading-snug"
          >
            Atomic Edu already knows your classroom.
          </p>
        </div>
      </section>

      {/* (d) Proof strip — one honest line, no fabricated testimonials */}
      <section
        data-testid="home-proof-strip"
        className="relative z-10 py-8 md:py-10 bg-[#96ded1]/15 border-y border-[#96ded1]/40"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p
            data-testid="home-proof-strip-line"
            className="font-bold text-[#1c2d3d] text-base md:text-lg tracking-tight leading-snug"
          >
            Built and used daily in a real Ontario classroom.
          </p>
        </div>
      </section>

      {/* (e) Pricing teaser — no dollar figures, no dates */}
      <section
        data-testid="home-pricing-teaser"
        className="relative z-10 py-14 md:py-16"
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p
            data-testid="home-pricing-teaser-anchor"
            className="font-black text-xl md:text-2xl text-[#1c2d3d] tracking-tight leading-snug"
          >
            From $40/year — early-bird pricing ends October 31.
          </p>
          <p
            data-testid="home-pricing-teaser-primary"
            className="mt-3 font-extrabold text-lg md:text-xl text-[#1c2d3d] tracking-tight leading-snug"
          >
            7-day free trial. No credit card to start.{" "}
            <Link
              to="/pricing"
              data-testid="home-pricing-teaser-see-pricing"
              className="text-[#4a6984] underline underline-offset-4 hover:text-[#1c2d3d] transition-colors"
            >
              See pricing →
            </Link>
          </p>
          <p
            data-testid="home-pricing-teaser-schools"
            className="mt-4 italic text-[#4a6984] font-medium text-[15px] md:text-base"
          >
            Buying for a school or board?{" "}
            <Link
              to="/contact"
              data-testid="home-pricing-teaser-email-me"
              className="not-italic font-bold text-[#1c2d3d] underline underline-offset-4 hover:text-[#4a6984] transition-colors"
            >
              Email me.
            </Link>
          </p>
        </div>
      </section>
      {/* ============================================================
          END LANE 1 inserts.
      ============================================================ */}

      {/* TODAY'S SKILL IN THE WILD — daily nudge on the home page.
          The actual generator now lives at /generator. Card-heavy
          layout on desktop: text card takes 3/5 of the row so it
          reads with less wrapping, illustration takes 2/5 (Phase 124
          downsize per user request). Stacks on mobile.
          Phase 137 — Logged-out users see a locked card here which is
          a dead end. Wrap in `{user && ...}` so this entire section is
          logged-in only. Logged-out marketing visitors skip straight
          from the demo to the Hub and Career Connection. */}
      {user && (
      <section
        data-testid="home-sitw-section"
        className="relative z-10 pt-8 pb-12 md:pt-10 md:pb-14 border-t border-[#4a6984]/5"
      >
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-6 md:gap-8 items-start">
            <div>
              <SkillInTheWildCard refreshKey={coverageBump} />
            </div>
            {/* Mobile: hide the decorative illustration per spec #7 —
                the SkillInTheWildCard already conveys the message;
                showing only the text card keeps the mobile scroll
                tight. The image is also constrained to a smaller
                max-width on desktop so it doesn't dominate the row. */}
            <div className="hidden md:block">
              <SkillInTheWildImage
                refreshKey={coverageBump}
                className="max-w-[300px] ml-auto"
              />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Lane 1 (Feb 2026) — Career Connection section RELOCATED to
          /the-path. See ThePath.jsx :: "path-career-connection" for the
          new home. Homepage now stays focused on the loop → privacy →
          objection → proof → pricing teaser chain. */}

      {/* Phase 137 — GROWTH OVER TIME section removed per homepage
          restructure. Abstract progression argument moved off the
          marketing page; lives in /the-path. */}

      {/* Phase 133 — Classroom Support Hub section RELOCATED from
          top-of-page (Phase 126.I origin) to this slot, between
          Growth Over Time and Core Value. New strategic role: a
          deepening signal of pedagogical seriousness AFTER the
          framework + growth story has earned attention. Includes a
          new one-line lead-in directly above the section so the
          relocation is obviously intentional, not buried. */}
      <section
        id="classroom-support-hub"
        data-testid="hub-marketing-section"
        className="relative z-10 py-16 md:py-20 bg-[#96ded1]/10 border-t border-[#4a6984]/5 scroll-mt-24"
      >
        <div className="max-w-5xl mx-auto px-6">
          <p
            data-testid="hub-leadin"
            className="text-center font-extrabold text-xl sm:text-2xl text-[#1c2d3d] tracking-tight leading-tight max-w-3xl mx-auto"
          >
            Have diverse needs in your classroom? Walk in with a plan.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
            <div className="md:col-span-7">
              <p className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70 flex items-center gap-2">
                <LifeBuoy className="h-4 w-4" /> Free · No login required
              </p>
              <h2 className="mt-3 font-extrabold text-3xl sm:text-4xl text-[#1c2d3d] tracking-tight leading-tight">
                Classroom Support Hub
              </h2>
              <p className="mt-5 text-[#4a6984] font-medium leading-relaxed">
                Teacher-to-teacher guides for ADHD, gifted, autistic, and twice-exceptional
                students — strengths first, classroom strategy only, never a label.
                Plus two free 1-page-plan generators built on the same shared
                strategy library that powers the Individual Student Plan.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/classroom-support"
                  data-testid="hub-marketing-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1c2d3d] text-white font-extrabold text-sm hover:bg-[#0f1e2c] transition"
                >
                  Open the Hub <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/posters"
                  data-testid="hub-marketing-posters"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#1c2d3d] font-extrabold text-sm border border-[#4a6984]/30 hover:bg-[#96ded1]/15 transition"
                >
                  Free classroom posters
                </Link>
              </div>
            </div>
            <ul className="md:col-span-5 space-y-4 text-sm text-[#4a6984] leading-relaxed">
              {[
                {
                  key: "adhd",
                  name: "ADHD in the Classroom",
                  to: "/classroom-support/adhd",
                  after: " — movement, chunks, the 4-to-1 ratio, choice of output.",
                },
                {
                  key: "giftedness",
                  name: "Giftedness in the Classroom",
                  to: "/classroom-support/giftedness",
                  after: " — depth over volume, productive-failure framing.",
                },
                {
                  key: "autism",
                  name: "Autism in the Classroom",
                  to: "/classroom-support/autism",
                  after: " — predictability, sensory awareness, and respecting how a student communicates.",
                },
                {
                  key: "2e",
                  name: "Twice-Exceptional (2e)",
                  to: "/classroom-support/2e",
                  after: " — the bright-and-struggling profile that hides for years.",
                },
              ].map(({ key, name, to, after }) => (
                <li
                  key={key}
                  className="flex items-start"
                  data-testid={`hub-marketing-profile-${key}`}
                >
                  <span className="ae-marker mt-2.5" />
                  <span>
                    <Link
                      to={to}
                      data-testid={`hub-marketing-profile-${key}-link`}
                      className="inline-block font-extrabold text-[#1c2d3d] underline decoration-[#96ded1] decoration-2 underline-offset-4 hover:decoration-[#4a6984] hover:text-[#4a6984] transition-colors py-2.5 -my-2.5 rounded-sm"
                    >
                      {name}
                    </Link>
                    {after}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Phase 137 — CORE VALUE section removed per homepage
          restructure. The "Reclaim your evenings" message lives on
          the differentiator card up top. Replaced here by a single
          clean closing CTA. */}

      {/* Phase 137 — Closing CTA. Replaces the sticky mobile footer
          on the homepage with a single clean trial CTA above the
          site footer. Same primary action as the hero — no
          competing "Start Building from $0" alt-link. */}
      <section
        data-testid="closing-cta-section"
        aria-labelledby="closing-cta-heading"
        className="relative z-10 py-20 md:py-24 bg-[#96ded1]/10 border-t border-[#4a6984]/10"
      >
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p
            data-testid="closing-cta-kicker"
            className="font-bold uppercase text-xs tracking-[0.25em] text-[#4a6984]/70"
          >
            Ready when you are
          </p>
          <h2
            id="closing-cta-heading"
            data-testid="closing-cta-heading"
            className="mt-4 font-extrabold text-3xl sm:text-4xl text-[#1c2d3d] tracking-tight leading-tight"
          >
            Start your 7-day free trial.
          </h2>
          <p
            data-testid="closing-cta-subtext"
            className="mt-4 text-[#4a6984] font-medium text-base sm:text-lg leading-relaxed"
          >
            No credit card required. Cancel anytime.
          </p>
          <div className="mt-8 flex justify-center">
            <Button
              asChild
              data-testid="closing-cta-button"
              className="ae-cta h-12 px-8 rounded-lg text-base inline-flex items-center gap-2"
            >
              <Link to={user ? "/generator" : "/pricing"}>
                {user ? "Open Atomic Edu" : "Start your free trial"}{" "}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Early access waitlist removed Feb 2026 — launch is live, no
          need to misleadingly promise a "coming soon" experience.
          Will revisit when we want a proper newsletter signup. */}

      {/* Classroom Support Hub marketing section moved to the top of
          the page (directly below the header) Feb 2026. The Hub now
          replaces the daily-activity card slot, per user request, to
          maximize visibility of the free top-of-funnel layer. */}

      {/* FOOTER — removed Feb 2026: the shared `<SiteFooter />` mounted
          in App.js now carries the merged copy (crafted-in-Canada line
          + Teacher Dashboard/Classroom Support/Pricing/Privacy/Terms/
          Contact nav + © line). Keeping a page-level footer here caused
          the double-footer regression flagged on mobile. */}
    </div>
  );
};

export default AtomicEdu;
